import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight, Check, Brain, Zap, ShieldAlert, Globe, FileText,
  TrendingDown, Repeat, Frown, BarChart3, Bell, MapPin, Languages,
  Sparkles, Users, BookOpen, Crown, ChevronRight, Star,
} from "lucide-react";

// ─── Reveal-on-scroll wrapper (CSS-only, IntersectionObserver) ───
function Reveal({ children, delay = 0, as: As = "div", className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <As
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </As>
  );
}

// ─── Tiny dashboard mockup (SVG, no external assets) ───
function HeroMockup() {
  return (
    <svg viewBox="0 0 720 460" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f0f0f" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00ff88" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00ff88" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Browser frame */}
      <rect x="0" y="0" width="720" height="460" rx="14" fill="url(#bgGrad)" stroke="#1f1f1f" strokeWidth="1" />
      <circle cx="22" cy="22" r="5" fill="#3a3a3a" />
      <circle cx="40" cy="22" r="5" fill="#3a3a3a" />
      <circle cx="58" cy="22" r="5" fill="#00ff88" opacity="0.6" />
      <rect x="80" y="14" width="200" height="16" rx="3" fill="#1a1a1a" />
      <text x="92" y="25" fontFamily="monospace" fontSize="9" fill="#666">braintrades.ai/dashboard</text>
      {/* Divider */}
      <line x1="0" y1="44" x2="720" y2="44" stroke="#1f1f1f" />
      {/* KPI cards row */}
      {[
        { x: 24, label: "TOTAL P&L", val: "+₹4,82,150", color: "#00ff88" },
        { x: 196, label: "WIN RATE", val: "68.4%", color: "#ffffff" },
        { x: 368, label: "AVG R", val: "+2.14R", color: "#00ff88" },
        { x: 540, label: "TRADES", val: "127", color: "#ffffff" },
      ].map((k) => (
        <g key={k.label}>
          <rect x={k.x} y="60" width="160" height="74" rx="6" fill="#111111" stroke="#1f1f1f" />
          <text x={k.x + 14} y="80" fontFamily="Inter, sans-serif" fontSize="8" fill="#888" letterSpacing="1.5">{k.label}</text>
          <text x={k.x + 14} y="115" fontFamily="Inter, sans-serif" fontSize="22" fontWeight="800" fill={k.color}>{k.val}</text>
        </g>
      ))}
      {/* Chart area */}
      <rect x="24" y="152" width="676" height="200" rx="6" fill="#111111" stroke="#1f1f1f" />
      <text x="40" y="174" fontFamily="Inter, sans-serif" fontSize="9" fill="#888" letterSpacing="1.5">CUMULATIVE P&amp;L</text>
      {/* Chart grid */}
      {[1, 2, 3].map((i) => (
        <line key={`grid-${i}`} x1="40" y1={210 + i * 35} x2="690" y2={210 + i * 35} stroke="#1a1a1a" strokeDasharray="3 4" />
      ))}
      {/* Chart line */}
      <path
        d="M 40 320 L 110 305 L 170 290 L 230 275 L 290 245 L 350 252 L 410 220 L 470 200 L 530 195 L 600 175 L 690 165"
        fill="none" stroke="#00ff88" strokeWidth="2.5" strokeLinejoin="round"
      />
      <path
        d="M 40 320 L 110 305 L 170 290 L 230 275 L 290 245 L 350 252 L 410 220 L 470 200 L 530 195 L 600 175 L 690 165 L 690 340 L 40 340 Z"
        fill="url(#chartFill)"
      />
      {/* Dots */}
      {[[110,305],[230,275],[350,252],[470,200],[600,175],[690,165]].map(([x,y]) => (
        <circle key={`dot-${x}-${y}`} cx={x} cy={y} r="3.5" fill="#0a0a0a" stroke="#00ff88" strokeWidth="2" />
      ))}
      {/* Bottom heatmap row */}
      <rect x="24" y="370" width="676" height="70" rx="6" fill="#111111" stroke="#1f1f1f" />
      <text x="40" y="392" fontFamily="Inter, sans-serif" fontSize="9" fill="#888" letterSpacing="1.5">LAST 12 WEEKS</text>
      {Array.from({ length: 36 }).map((_, i) => {
        const intensity = [0.1,0.25,0.4,0.6,0.85,1][Math.floor(Math.random()*6)];
        const isLoss = Math.random() < 0.2;
        return (
          <rect key={`heat-${i}`} x={42 + i * 17} y="402" width="13" height="28"
            fill={isLoss ? "#ff3b30" : "#00ff88"} opacity={intensity} rx="2" />
        );
      })}
    </svg>
  );
}

// ─── Wordmark used across nav + footer ───
function Wordmark({ size = "text-xl" }) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="w-8 h-8 rounded-md flex items-center justify-center"
        style={{ background: "#00ff88" }}
      >
        <Brain className="w-4 h-4 text-black" strokeWidth={2.5} />
      </span>
      <span className={`${size} font-extrabold tracking-tight`} style={{ fontFamily: "Inter, sans-serif" }}>
        Brain<span style={{ color: "#00ff88" }}>TRADES</span>
      </span>
    </div>
  );
}

// ─── Reusable card ───
function FeatureCard({ icon: Icon, emoji, title, body, accent }) {
  return (
    <div
      data-testid={`feature-${title.split(" ")[0].toLowerCase()}`}
      className="group relative p-7 rounded-xl border transition-all duration-300 hover:-translate-y-1"
      style={{ background: "#111111", borderColor: "#1f1f1f" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#00ff88"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1f1f1f"; }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-xl"
          style={{ background: "rgba(0,255,136,0.08)", border: "1px solid rgba(0,255,136,0.2)" }}
        >
          {Icon ? <Icon className="w-5 h-5" style={{ color: accent || "#00ff88" }} /> : <span>{emoji}</span>}
        </div>
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: "#888" }}>{body}</p>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const primaryCTA = isAuthed ? "/dashboard" : "/register";
  const primaryCTALabel = isAuthed ? "Go to Dashboard" : "Start Free — No Card Needed";

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen text-white"
      style={{
        background: "#0a0a0a",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* ═══ NAVBAR ═══ */}
      <header
        data-testid="landing-nav"
        className="sticky top-0 z-50 border-b"
        style={{
          background: "rgba(10,10,10,0.7)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderColor: "#1f1f1f",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo"><Wordmark /></Link>
          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: "Features", href: "#features" },
              { label: "Pricing", href: "#pricing" },
              { label: "Blog", href: "#blog" },
            ].map((l) => (
              <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`}
                 className="text-sm font-medium transition-colors duration-150 hover:text-white" style={{ color: "#888" }}>
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {!isAuthed && (
              <Link to="/login" data-testid="nav-signin"
                    className="hidden sm:inline-block text-sm font-medium text-[#888] hover:text-white transition-colors">
                Sign in
              </Link>
            )}
            <Link
              to={primaryCTA}
              data-testid="nav-cta"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-all hover:scale-[1.03]"
              style={{ background: "#00ff88", color: "#000" }}
            >
              {isAuthed ? "Dashboard" : "Start Free"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section data-testid="hero" className="relative overflow-hidden">
        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0,255,136,0.12) 0%, rgba(10,10,10,0) 60%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
                    style={{ background: "rgba(0,255,136,0.08)", color: "#00ff88", border: "1px solid rgba(0,255,136,0.25)" }}>
                <Sparkles className="w-3 h-3" /> India's #1 Trading Journal · Crypto · Equity · Forex
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02]">
                Stop Trading Blind.<br />
                Start Trading <span style={{ color: "#00ff88" }}>Smart.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-7 text-lg sm:text-xl leading-relaxed max-w-xl" style={{ color: "#888" }}>
                95% of Indian traders lose money because they never track their trades.
                <span className="text-white font-medium"> BrainTrades shows you exactly why you're losing — and how to fix it.</span>
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={primaryCTA} data-testid="hero-primary-cta"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-bold transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(0,255,136,0.35)]"
                      style={{ background: "#00ff88", color: "#000" }}>
                  {primaryCTALabel} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#how" data-testid="hero-secondary-cta"
                   className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-bold border transition-colors hover:bg-white/5"
                   style={{ borderColor: "#1f1f1f", color: "#fff" }}>
                  See How It Works
                </a>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-7 flex items-center gap-3 text-sm" style={{ color: "#666" }}>
                <div className="flex -space-x-2">
                  {["#00ff88", "#ffaa00", "#5cd8ff", "#ff7ab6"].map((c, i) => (
                    <div key={c} className="w-7 h-7 rounded-full border-2"
                         style={{ background: c, borderColor: "#0a0a0a", opacity: 0.85 - i * 0.1 }} />
                  ))}
                </div>
                <span>Join <span className="text-white font-semibold">500+ traders</span> already journaling</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={200} className="relative">
            <div className="relative rounded-2xl border shadow-2xl overflow-hidden"
                 style={{ borderColor: "#1f1f1f", boxShadow: "0 25px 80px -20px rgba(0,255,136,0.18)" }}>
              <HeroMockup />
            </div>
            {/* Floating accent */}
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full blur-3xl"
                 style={{ background: "#00ff88", opacity: 0.15 }} />
          </Reveal>
        </div>
      </section>

      {/* ═══ PROBLEM ═══ */}
      <section data-testid="problem" className="relative py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: "#00ff88" }}>
              Sound familiar?
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight max-w-3xl">
              You're losing money in trades<br />
              you don't even <span style={{ color: "#00ff88" }}>understand.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              { emoji: "😤", icon: Frown, title: "Liquidated by signals", body: "You follow Telegram signals blindly and still get liquidated. Again." },
              { emoji: "📉", icon: TrendingDown, title: "No clue what works", body: "You don't know which setups are actually working for you. It's all a blur." },
              { emoji: "🔁", icon: Repeat, title: "Same mistakes, every week", body: "FOMO. Revenge trades. Oversizing. You repeat them and don't realize until your account is gone." },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="p-7 rounded-xl border h-full" style={{ background: "#111", borderColor: "#1f1f1f" }}>
                  <div className="text-3xl mb-4">{p.emoji}</div>
                  <h3 className="text-lg font-bold mb-2">{p.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#888" }}>{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={300}>
            <p className="mt-12 text-xl sm:text-2xl font-bold text-center max-w-3xl mx-auto leading-snug">
              The problem isn't the market.<br />
              <span style={{ color: "#00ff88" }}>It's that you're not tracking yourself.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ SOLUTION / FEATURES ═══ */}
      <section id="features" data-testid="features" className="py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: "#00ff88" }}>
              Meet BrainTrades
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight max-w-3xl">
              Your personal trading coach.<br />
              <span style={{ color: "#888" }}>Available 24/7.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { emoji: "🧠", title: "Why You're Losing AI", body: "Detects your losing patterns automatically. See exactly which emotion (FOMO, revenge, overtrading) is bleeding your account." },
              { emoji: "📊", title: "Full P&L Dashboard", body: "Cumulative equity curve, win rate, Risk:Reward — see your real performance, not vanity wins." },
              { emoji: "🔔", title: "Liquidation Protector", body: "Calculates your risk before you click buy. Stop blowing accounts on oversized trades." },
              { emoji: "🇮🇳", title: "Built for India", body: "Delta Exchange, Zerodha, CoinDCX, Angel One, Fyers, XM, Exness — all your brokers, one journal." },
              { emoji: "📄", title: "Expiry Day Report", body: "Special F&O and options tracking. See your Thursday performance separately from your swing trades." },
              { emoji: "🌐", title: "Hindi + English", body: "Journal in the language you actually think in. हिंदी में या English में — your call." },
            ].map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 80}>
                <FeatureCard emoji={f.emoji} title={f.title} body={f.body} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how" data-testid="how-it-works" className="py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: "#00ff88" }}>
              How it works
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              Three steps to <span style={{ color: "#00ff88" }}>profitability.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
            {/* Connector line — desktop only */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px"
                 style={{ background: "linear-gradient(90deg, transparent, #1f1f1f, #00ff88, #1f1f1f, transparent)" }} />
            {[
              { n: "01", title: "Log your trade", body: "Add it in 15 seconds — date, broker, symbol, entry/exit, stop loss, emotion tag, notes. Manual or auto-import (Pro)." },
              { n: "02", title: "BrainTrades analyzes", body: "Pattern of the Week banner shows your worst emotion. Per-symbol, per-broker breakdowns reveal what's actually working." },
              { n: "03", title: "Fix mistakes, grow", body: "Stop trading the patterns that lose money. Double down on the ones that win. Watch your equity curve bend upward." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 120}>
                <div className="relative p-7 rounded-xl border h-full text-center" style={{ background: "#111", borderColor: "#1f1f1f" }}>
                  <div className="mx-auto mb-5 w-14 h-14 rounded-full flex items-center justify-center font-black text-lg"
                       style={{ background: "#0a0a0a", border: "2px solid #00ff88", color: "#00ff88" }}>
                    {s.n}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#888" }}>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHO IS THIS FOR ═══ */}
      <section data-testid="audience" className="py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: "#00ff88" }}>
              Who is this for
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              Built for <span style={{ color: "#00ff88" }}>every</span> Indian trader.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              { tag: "Crypto", title: "Crypto traders", line: "Delta Exchange · CoinDCX · Binance · WazirX",
                items: ["Track perp futures & options", "Liquidation tracking", "USDT pairs supported"] },
              { tag: "Equity", title: "Equity traders", line: "Zerodha · Angel One · Dhan · Fyers · Upstox",
                items: ["NIFTY, BANKNIFTY, F&O", "Expiry day breakdowns", "Tax-ready exports"] },
              { tag: "Forex", title: "Forex traders", line: "XM · Exness · IC Markets",
                items: ["EURUSD, GBPUSD, XAUUSD", "Multi-currency P&L", "Session-wise analytics"] },
            ].map((a, i) => (
              <Reveal key={a.title} delay={i * 100}>
                <div className="p-7 rounded-xl border h-full hover:-translate-y-1 transition-transform"
                     style={{ background: "#111", borderColor: "#1f1f1f" }}>
                  <span className="inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded"
                        style={{ background: "rgba(0,255,136,0.1)", color: "#00ff88" }}>
                    {a.tag}
                  </span>
                  <h3 className="mt-4 text-2xl font-bold">{a.title}</h3>
                  <p className="mt-1 text-sm font-mono" style={{ color: "#666" }}>{a.line}</p>
                  <ul className="mt-5 space-y-2">
                    {a.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm" style={{ color: "#cccccc" }}>
                        <Check className="w-4 h-4 shrink-0" style={{ color: "#00ff88" }} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" data-testid="pricing" className="py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-center" style={{ color: "#00ff88" }}>
              Pricing
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-center">
              Start free. <span style={{ color: "#00ff88" }}>Upgrade when it pays for itself.</span>
            </h2>
            <p className="mt-5 text-center max-w-xl mx-auto" style={{ color: "#888" }}>
              One winning trade pays for a year of Pro. We're confident enough to bet on that.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-5 items-start">
            {/* FREE */}
            <Reveal>
              <div className="p-8 rounded-xl border h-full flex flex-col" style={{ background: "#111", borderColor: "#1f1f1f" }}>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-5 h-5" style={{ color: "#888" }} />
                  <h3 className="text-lg font-bold">Free</h3>
                </div>
                <p className="text-sm" style={{ color: "#888" }}>Start journaling today</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="text-5xl font-black">₹0</span>
                  <span className="pb-2 text-sm" style={{ color: "#888" }}>/ forever</span>
                </div>
                <ul className="mt-7 space-y-3 flex-1">
                  {["Basic trading journal", "Up to 10 trades / month", "P&L dashboard", "CSV export", "INR + USD toggle"].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "#cccccc" }}>
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#888" }} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid="pricing-free-cta"
                      className="mt-8 inline-flex items-center justify-center px-5 py-3 rounded-md font-bold border transition-colors hover:bg-white/5"
                      style={{ borderColor: "#1f1f1f", color: "#fff" }}>
                  Start free
                </Link>
              </div>
            </Reveal>

            {/* PRO — featured */}
            <Reveal delay={100}>
              <div className="relative p-8 rounded-xl border-2 h-full flex flex-col"
                   style={{ background: "#0d1410", borderColor: "#00ff88", boxShadow: "0 0 50px -10px rgba(0,255,136,0.25)" }}>
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded"
                      style={{ background: "#00ff88", color: "#000" }}>
                  Most Popular
                </span>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5" style={{ color: "#00ff88" }} />
                  <h3 className="text-lg font-bold">Pro</h3>
                </div>
                <p className="text-sm" style={{ color: "#888" }}>For serious traders</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="text-5xl font-black">₹999</span>
                  <span className="pb-2 text-sm" style={{ color: "#888" }}>/ month</span>
                </div>
                <ul className="mt-7 space-y-3 flex-1">
                  {[
                    "Everything in Free, plus:",
                    "Unlimited trades",
                    "AI pattern detection",
                    "PDF reports + share cards",
                    "Broker auto-import",
                    "Calendar heatmap & R-analytics",
                    "Pattern of the Week coach",
                  ].map((f, i) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${i === 0 ? "font-semibold" : ""}`}
                        style={{ color: i === 0 ? "#fff" : "#cccccc" }}>
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#00ff88" }} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid="pricing-pro-cta"
                      className="mt-8 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-md font-bold transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(0,255,136,0.4)]"
                      style={{ background: "#00ff88", color: "#000" }}>
                  Get Pro <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>

            {/* ELITE */}
            <Reveal delay={200}>
              <div className="p-8 rounded-xl border h-full flex flex-col" style={{ background: "#111", borderColor: "#1f1f1f" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5" style={{ color: "#ffaa00" }} />
                  <h3 className="text-lg font-bold">Elite</h3>
                </div>
                <p className="text-sm" style={{ color: "#888" }}>Pro traders & funds</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="text-5xl font-black">₹2,999</span>
                  <span className="pb-2 text-sm" style={{ color: "#888" }}>/ month</span>
                </div>
                <ul className="mt-7 space-y-3 flex-1">
                  {[
                    "Everything in Pro, plus:",
                    "1-on-1 onboarding call",
                    "Priority WhatsApp support",
                    "Advanced AI coaching reports",
                    "Team / family accounts (5 seats)",
                    "API access & custom exports",
                    "Early access to new features",
                  ].map((f, i) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${i === 0 ? "font-semibold" : ""}`}
                        style={{ color: i === 0 ? "#fff" : "#cccccc" }}>
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#ffaa00" }} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid="pricing-elite-cta"
                      className="mt-8 inline-flex items-center justify-center px-5 py-3 rounded-md font-bold border transition-colors hover:bg-white/5"
                      style={{ borderColor: "#1f1f1f", color: "#fff" }}>
                  Go Elite
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section data-testid="testimonials" className="py-24 border-t" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: "#00ff88" }}>
              Real traders
            </p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              What Indian traders <span style={{ color: "#00ff88" }}>are saying.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Rohan M.", niche: "Crypto · Delta Exchange", color: "#00ff88",
                quote: "I was getting liquidated every Sunday on BTC. BrainTrades showed me my FOMO trades were 78% losers. Cut them out. Account is finally growing.",
              },
              {
                name: "Priya S.", niche: "Equity · Zerodha", color: "#ffaa00",
                quote: "The expiry day report was a wake-up call — I was net negative every Thursday. Switched to swing-only on Thursdays. Up ₹4.2L in 3 months.",
              },
              {
                name: "Aditya K.", niche: "Forex · XM", color: "#5cd8ff",
                quote: "Best part is the Hindi support. I write notes in Hinglish like I actually think. The Pattern of the Week feature is wild — feels personal.",
              },
            ].map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <div className="p-7 rounded-xl border h-full flex flex-col" style={{ background: "#111", borderColor: "#1f1f1f" }}>
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="w-4 h-4 fill-current" style={{ color: "#ffaa00" }} />
                    ))}
                  </div>
                  <p className="text-base leading-relaxed flex-1" style={{ color: "#cccccc" }}>
                    "{t.quote}"
                  </p>
                  <div className="mt-6 flex items-center gap-3 pt-5 border-t" style={{ borderColor: "#1f1f1f" }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-black"
                         style={{ background: t.color }}>
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-bold">{t.name}</div>
                      <div className="text-xs" style={{ color: "#666" }}>{t.niche}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section data-testid="final-cta" className="relative py-24 sm:py-32 border-t overflow-hidden" style={{ borderColor: "#1f1f1f" }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,255,136,0.18) 0%, rgba(10,10,10,0) 70%)",
        }} />
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #00ff88, transparent)" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
              Your next trade<br />
              could be your <span style={{ color: "#00ff88" }}>best one.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 text-lg sm:text-xl max-w-2xl mx-auto" style={{ color: "#888" }}>
              Start journaling free today. No credit card. No excuses.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to={primaryCTA} data-testid="final-cta-btn"
                  className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-md text-lg font-black transition-all hover:scale-105 hover:shadow-[0_0_60px_rgba(0,255,136,0.5)]"
                  style={{ background: "#00ff88", color: "#000" }}>
              Start Journaling Free <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
          <Reveal delay={280}>
            <p className="mt-6 text-sm" style={{ color: "#666" }}>
              Free forever. No credit card. Cancel anytime.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer data-testid="landing-footer" className="border-t py-14" style={{ borderColor: "#1f1f1f" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
            <div>
              <Wordmark />
              <p className="mt-4 text-sm max-w-xs" style={{ color: "#888" }}>
                The trading journal built for Indian traders. Track every trade. Master your edge.
              </p>
              <p className="mt-5 text-xs flex items-center gap-1.5" style={{ color: "#666" }}>
                Made with care for Indian Traders <span aria-label="India">🇮🇳</span>
              </p>
            </div>
            {[
              { title: "Product", links: [["Features", "#features"], ["Pricing", "#pricing"], ["Dashboard", "/dashboard"]] },
              { title: "Resources", links: [["Blog", "#blog"], ["Docs", "#docs"], ["Support", "mailto:hello@braintrades.ai"]] },
              { title: "Legal", links: [["Privacy", "#privacy"], ["Terms", "#terms"], ["Refund", "#refund"]] },
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: "#666" }}>{col.title}</h4>
                <ul className="space-y-3">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith("/") ? (
                        <Link to={href} className="text-sm hover:text-white transition-colors" style={{ color: "#aaa" }}>{label}</Link>
                      ) : (
                        <a href={href} className="text-sm hover:text-white transition-colors" style={{ color: "#aaa" }}>{label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4"
               style={{ borderColor: "#1f1f1f" }}>
            <p className="text-xs" style={{ color: "#666" }}>© 2025 BrainTrades. All rights reserved.</p>
            <p className="text-xs font-mono" style={{ color: "#666" }}>braintrades.ai</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
